import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SignOut, House, Users, Storefront, Envelope, UsersThree, ArrowsClockwise, Wrench, DownloadSimple } from "@phosphor-icons/react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { adminSummary, adminContacts, adminDrivers, adminMerchants, adminServicePros, adminWaitlist } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import Logo from "@/components/brand/Logo";
import useSeo from "@/lib/useSeo";
import { toast } from "sonner";

const TABS = [
  { id: "contacts", label: "Contacts", icon: Envelope, color: "#FF7A45" },
  { id: "drivers", label: "Drivers", icon: Users, color: "#14B8A6" },
  { id: "merchants", label: "Merchants", icon: Storefront, color: "#D4AF37" },
  { id: "service_pros", label: "Service Pros", icon: Wrench, color: "#5EEAD4" },
  { id: "waitlist", label: "Waitlist", icon: UsersThree, color: "#FB7185" },
];

const SUMMARY_KEY = {
  contacts: "contacts",
  drivers: "drivers",
  merchants: "merchants",
  service_pros: "service_pros",
  waitlist: "waitlist",
};

const MERCHANT_CATEGORIES = [
  { v: "all", l: "All" },
  { v: "delivery_partner", l: "Delivery Partner" },
  { v: "handyman", l: "Handyman" },
  { v: "cleaning", l: "Cleaning" },
  { v: "tech_support", l: "Tech Support" },
  { v: "events", l: "Events" },
  { v: "retail", l: "Retail" },
  { v: "other", l: "Other" },
];

const SERVICE_PRO_FILTERS = [
  { v: "all", l: "All" },
  { v: "handyman", l: "Handyman" },
  { v: "cleaning", l: "Cleaning" },
  { v: "tech_support", l: "Tech Support" },
  { v: "events", l: "Events" },
];

const FETCHERS = {
  contacts: () => adminContacts(),
  drivers: () => adminDrivers(),
  merchants: (filter) => adminMerchants(filter),
  service_pros: (filter) => adminServicePros(filter),
  waitlist: () => adminWaitlist(),
};

export default function AdminDashboard() {
  useSeo({ title: "Admin Dashboard", description: "IslandHop admin dashboard." });
  const { user, logout } = useAdminAuth();
  const [tab, setTab] = useState("contacts");
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ count: 0, items: [], by_category: {}, by_service: {} });
  const [loading, setLoading] = useState(false);
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [proFilter, setProFilter] = useState("all");

  const refreshSummary = async () => {
    try {
      const s = await adminSummary();
      setSummary(s);
    } catch { /* ignore */ }
  };

  const loadTab = async (id, filter) => {
    setLoading(true);
    try {
      const res = await FETCHERS[id](filter);
      setData(res);
    } catch {
      toast.error("Couldn't load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshSummary(); }, []);

  useEffect(() => {
    if (tab === "merchants") loadTab("merchants", merchantFilter);
    else if (tab === "service_pros") loadTab("service_pros", proFilter);
    else loadTab(tab);
  }, [tab, merchantFilter, proFilter]);

  const breakdown = tab === "merchants" ? data.by_category || {} : tab === "service_pros" ? data.by_service || {} : null;
  const filters = tab === "merchants" ? MERCHANT_CATEGORIES : tab === "service_pros" ? SERVICE_PRO_FILTERS : null;
  const activeFilter = tab === "merchants" ? merchantFilter : proFilter;
  const setActiveFilter = tab === "merchants" ? setMerchantFilter : setProFilter;
  const totalForBreakdown = breakdown ? Object.values(breakdown).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="min-h-screen bg-ih-ink text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-black/60 backdrop-blur-xl border-b border-ih-line">
        <div className="max-w-[1600px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden md:inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-ih-teal">Admin Console</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site" className="text-sm text-white/60 hover:text-white transition inline-flex items-center gap-1.5">
              <House size={16} weight="bold" /> Site
            </Link>
            <span className="hidden md:inline text-sm text-white/60">{user?.email}</span>
            <button
              onClick={() => { logout(); toast.success("Signed out"); }}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-ih-line2 text-sm text-white/80 hover:border-rose-400/50 hover:text-rose-400 transition"
            >
              <SignOut size={14} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-5 lg:px-10 py-10 space-y-8">
        {/* Summary cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            const value = summary ? summary[SUMMARY_KEY[t.id]] : null;
            return (
              <div key={t.id} className="bg-ih-surface border border-ih-line rounded-2xl p-6 flex items-center justify-between" data-testid={`admin-stat-${t.id}`}>
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-white/40">{t.label}</p>
                  <p className="font-display font-extrabold text-3xl text-white mt-1">{value ?? "—"}</p>
                </div>
                <span className="w-12 h-12 rounded-xl bg-black/40 border border-ih-line2 grid place-items-center" style={{ color: t.color }}>
                  <Icon weight="duotone" size={22} />
                </span>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="inline-flex bg-ih-surface border border-ih-line rounded-full p-1 flex-wrap">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-4 py-2 text-sm rounded-full transition ${tab === t.id ? "bg-white text-black font-semibold" : "text-white/60 hover:text-white"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const cols = COLS[tab] || [];
                const filterSuffix = tab === "merchants" && merchantFilter !== "all" ? `-${merchantFilter}` :
                                     tab === "service_pros" && proFilter !== "all" ? `-${proFilter}` : "";
                const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
                downloadCsv(`islandhop-${tab}${filterSuffix}-${stamp}.csv`, data.items || [], cols);
                toast.success(`Exported ${data.items?.length || 0} rows`);
              }}
              disabled={!data.items || data.items.length === 0}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-ih-line2 hover:border-ih-teal/60 hover:text-ih-teal transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <DownloadSimple size={14} weight="bold" /> Export CSV
            </button>
            <button
              onClick={() => {
                if (tab === "merchants") loadTab("merchants", merchantFilter);
                else if (tab === "service_pros") loadTab("service_pros", proFilter);
                else loadTab(tab);
                refreshSummary();
              }}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-ih-line2 hover:border-white/30 transition"
            >
              <ArrowsClockwise size={14} weight="bold" /> Refresh
            </button>
          </div>
        </div>

        {/* Browse-by filter chips */}
        {filters && (
          <div className="flex flex-wrap gap-2" data-testid={`admin-filter-${tab}`}>
            {filters.map((f) => {
              const count = f.v === "all" ? totalForBreakdown : (breakdown[f.v] || 0);
              const active = activeFilter === f.v;
              return (
                <button
                  key={f.v}
                  onClick={() => setActiveFilter(f.v)}
                  data-testid={`admin-filter-${tab}-${f.v}`}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition ${
                    active
                      ? "bg-ih-teal text-black border-ih-teal font-semibold"
                      : "border-ih-line2 text-white/70 hover:text-white hover:border-white/30"
                  }`}
                >
                  {f.l}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${active ? "bg-black/15 text-black/70" : "bg-white/[0.06] text-white/50"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Table */}
        <div className="bg-ih-surface border border-ih-line rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-white/50">Loading…</div>
          ) : (
            <DataTable tab={tab} items={data.items} />
          )}
        </div>
      </div>
    </div>
  );
}

const COLS = {
  contacts: [
    { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString() },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "subject", label: "Subject" },
    { key: "message", label: "Message", truncate: 80 },
  ],
  drivers: [
    { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString() },
    { key: "full_name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "location", label: "Location" },
    { key: "vehicle_type", label: "Vehicle" },
    { key: "has_license", label: "Licensed", fmt: (v) => (v ? "Yes" : "No") },
    { key: "upstream_application_id", label: "Sync", render: (v) => <SyncPill synced={!!v} /> },
    { key: "sms_status", label: "SMS", render: (v) => <SmsPill status={v} /> },
  ],
  merchants: [
    { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString() },
    { key: "business_name", label: "Business" },
    { key: "contact_person", label: "Contact" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "category", label: "Category" },
    { key: "location", label: "Location" },
    { key: "upstream_application_id", label: "Sync", render: (v) => <SyncPill synced={!!v} /> },
    { key: "sms_status", label: "SMS", render: (v) => <SmsPill status={v} /> },
  ],
  service_pros: [
    { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString() },
    { key: "full_name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "service", label: "Service" },
    { key: "specialty", label: "Specialty", truncate: 40 },
    { key: "years_experience", label: "Exp." },
    { key: "has_insurance", label: "Insured", fmt: (v) => (v ? "Yes" : "No") },
    { key: "location", label: "Location" },
    { key: "upstream_application_id", label: "Sync", render: (v) => <SyncPill synced={!!v} /> },
  ],
  waitlist: [
    { key: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString() },
    { key: "email", label: "Email" },
    { key: "role", label: "Role" },
  ],
};

const SyncPill = ({ synced }) => (
  <span
    data-testid={`sync-pill-${synced ? "ok" : "pending"}`}
    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border ${
      synced
        ? "bg-ih-teal/10 text-ih-teal border-ih-teal/30"
        : "bg-amber-500/10 text-amber-300 border-amber-500/30"
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${synced ? "bg-ih-teal" : "bg-amber-400"}`} />
    {synced ? "Synced" : "Pending"}
  </span>
);

const SMS_LABEL = {
  delivered: { l: "Delivered", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", dot: "bg-emerald-400" },
  sent: { l: "Sent", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", dot: "bg-emerald-400" },
  queued: { l: "Queued", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30", dot: "bg-sky-400" },
  scheduled: { l: "Scheduled", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30", dot: "bg-sky-400" },
  accepted: { l: "Accepted", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30", dot: "bg-sky-400" },
  sending: { l: "Sending", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30", dot: "bg-sky-400" },
  undelivered: { l: "Undelivered", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", dot: "bg-rose-400" },
  failed: { l: "Failed", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", dot: "bg-rose-400" },
  skipped: { l: "Skipped", cls: "bg-white/[0.04] text-white/40 border-ih-line2", dot: "bg-white/30" },
  not_sent: { l: "No consent", cls: "bg-white/[0.04] text-white/40 border-ih-line2", dot: "bg-white/30" },
};

const SmsPill = ({ status }) => {
  const key = status || "not_sent";
  const meta = SMS_LABEL[key] || { l: key, cls: "bg-white/[0.04] text-white/60 border-ih-line2", dot: "bg-white/40" };
  return (
    <span
      data-testid={`sms-pill-${key}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border ${meta.cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.l}
    </span>
  );
};

const DataTable = ({ tab, items }) => {
  const cols = COLS[tab] || [];
  if (!items || items.length === 0) {
    return <div className="p-16 text-center text-white/50">No records yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={`admin-table-${tab}`}>
        <thead className="border-b border-ih-line">
          <tr>
            {cols.map((c) => (
              <th key={c.key} className="text-left px-5 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white/40">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={row.id || i} className="border-b border-ih-line last:border-0 hover:bg-white/[0.02]">
              {cols.map((c) => {
                const raw = row[c.key];
                if (c.render) {
                  return <td key={c.key} className="px-5 py-3 align-top">{c.render(raw, row)}</td>;
                }
                let value = c.fmt ? c.fmt(raw) : raw ?? "—";
                if (c.truncate && typeof value === "string" && value.length > c.truncate) {
                  value = value.slice(0, c.truncate) + "…";
                }
                return (
                  <td key={c.key} className="px-5 py-3 text-white/80 align-top">
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
